import type BABYLON from 'babylonjs';
import { extname } from 'loose-path';

import type { Color } from './color';
import { toColor3Option } from './color';
import type { Globals } from './globals';
import { compactOptions } from './options';

export function createEnvironmentTexture(
  globals: Globals,
  scene: BABYLON.Scene,
  urlOrTexture: string | BABYLON.BaseTexture
): BABYLON.BaseTexture {
  if (urlOrTexture instanceof globals.BABYLON.BaseTexture) return urlOrTexture;
  const ext = extname(new URL(urlOrTexture, globalThis.location.toString()).pathname);
  switch (ext) {
    case '.hdr':
      return new globals.BABYLON.HDRCubeTexture(urlOrTexture, scene, 256, false, true, false, true);
    default:
      return globals.BABYLON.CubeTexture.CreateFromPrefilteredData(urlOrTexture, scene);
  }
}

type EnvironmentHelperOptions = NonNullable<
  Parameters<BABYLON.Scene['createDefaultEnvironment']>[0]
>;

export interface EnvironmentOptions
  extends Omit<EnvironmentHelperOptions, 'groundColor' | 'skyboxColor' | 'environmentTexture'> {
  groundColor?: Color;
  skyboxColor?: Color;
  // environmentTexture is set to scene.
  // cf. https://github.com/BabylonJS/Babylon.js/blob/5.24.0/packages/dev/core/src/Helpers/environmentHelper.ts#L446-L458
}

function toEnvironmentHelperOptions(
  globals: Globals,
  { groundColor, skyboxColor, ...options }: EnvironmentOptions
): EnvironmentHelperOptions {
  return compactOptions({
    groundColor: toColor3Option(globals, groundColor)?.toLinearSpace(),
    skyboxColor: toColor3Option(globals, skyboxColor)?.toLinearSpace(),
    ...options,
  });
}

export function updateEnvironment(
  globals: Globals,
  helper: BABYLON.EnvironmentHelper,
  options: EnvironmentOptions = {}
): void {
  // The rootMesh position settings are broken when called in updateConfig.
  // cf. https://github.com/BabylonJS/Babylon.js/blob/5.23.0/packages/dev/core/src/Helpers/environmentHelper.ts#L487-L489
  const rootPosition = helper.rootMesh.position.clone();
  helper.updateOptions(toEnvironmentHelperOptions(globals, options));
  helper.rootMesh.position.set(rootPosition.x, rootPosition.y, rootPosition.z);
}

export function createEnvironment(
  globals: Globals,
  scene: BABYLON.Scene,
  options: EnvironmentOptions = {}
): BABYLON.Nullable<BABYLON.EnvironmentHelper> {
  return scene.createDefaultEnvironment(toEnvironmentHelperOptions(globals, options));
}
